// API Service for Admin Panel
//
// Priority:
// 1) REACT_APP_API_URL (recommended)
// 2) Local dev fallback (localhost)
// 3) Production fallback
const getDefaultApiBaseUrl = () => {
  try {
    const host = window.location.hostname;
    const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
    if (isLocalhost) return 'http://localhost:5000/api';
  } catch {
    // window not available (tests/build tools)
  }

  return 'https://backend.menubazzar.com/api';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getDefaultApiBaseUrl();

// Helper function to get auth token
const getToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to set auth token
const setToken = (token) => {
  localStorage.setItem('authToken', token);
};

// Helper function to remove auth token
const removeToken = () => {
  localStorage.removeItem('authToken');
};

// Helper function to get restaurant ID
const getRestaurantId = () => {
  return localStorage.getItem('restaurantId');
};

// Helper function to set restaurant ID
const setRestaurantId = (id) => {
  localStorage.setItem('restaurantId', id);
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  const config = {
    ...options,
    // IMPORTANT: merge headers AFTER spreading options so `options.headers` doesn't overwrite our defaults.
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(options.headers || {}),
    },
  };

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    // Ensure endpoint starts with / if API_BASE_URL doesn't end with /
    const url = API_BASE_URL.endsWith('/') 
      ? `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`
      : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    const response = await fetch(url, config);
    
    // Handle non-JSON responses
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.ok) {
      throw new Error(data.message || `API request failed: ${response.status} ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    // Provide more helpful error messages
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error('Unable to connect to server. Please check your internet connection and ensure the backend is running.');
    }
    throw error;
  }
};

// Authentication APIs
export const signupRequestsAPI = {
  request: async (email) => {
    return apiRequest('/auth/signup-requests', {
      method: 'POST',
      body: { email }
    });
  },
  get: async (id) => {
    return apiRequest(`/auth/signup-requests/${id}`);
  },
  listMine: async () => {
    return apiRequest('/auth/signup-requests/me/list');
  },
  respond: async (id, decision) => {
    return apiRequest(`/auth/signup-requests/${id}/respond`, {
      method: 'POST',
      body: { decision }
    });
  }
};

export const authAPI = {
  getAuthEmail: () => {
    // Decode JWT payload in a safe way to read email (frontend convenience only).
    try {
      const token = getToken();
      if (!token) return '';
      const [, payload] = token.split('.');
      if (!payload) return '';
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return String(json.email || '');
    } catch {
      return '';
    }
  },
  login: async (email, password) => {
    // Admin login should use the restricted admin endpoint
    const response = await apiRequest('/auth/admin/login', {
      method: 'POST',
      body: { email, password },
    });
    
    if (response.data?.token) {
      setToken(response.data.token);
      setRestaurantId(response.data.restaurant.id);
    }
    
    return response;
  },

  verify: async () => {
    return apiRequest('/auth/verify');
  },

  logout: () => {
    removeToken();
    localStorage.removeItem('restaurantId');
  },

  isAuthenticated: () => {
    return !!getToken();
  },

  restrictedSignup: async (data) => {
    // Support both snake_case and camelCase in the API client
    const payload = {
      restaurantName: data.restaurantName || data.restaurant_name,
      location: data.location,
      numberOfTables: data.numberOfTables || data.number_of_tables,
      adminEmail: data.adminEmail || data.admin_email,
      password: data.password
    };

    const supabaseAccessToken = data.supabaseAccessToken || data.supabase_access_token;
    const inviteToken = data.signupInviteToken || data.signup_invite_token;

    console.log('Sending signup payload:', payload);

    const response = await apiRequest('/auth/restricted-signup', {
      method: 'POST',
      headers: {
        ...(supabaseAccessToken ? { 'X-Supabase-Access-Token': supabaseAccessToken } : {}),
        ...(inviteToken ? { 'X-Signup-Invite-Token': inviteToken } : {})
      },
      body: { ...payload, ...(inviteToken ? { signupInviteToken: inviteToken } : {}) }
    });

    if (response.data?.token) {
      setToken(response.data.token);
      setRestaurantId(response.data.restaurant?.id);
    }

    return response;
  },
};

// Restaurant APIs
export const platformAPI = {
  getRestaurantsOverview: async ({ restaurantId = '', from = '', to = '' } = {}) => {
    const params = new URLSearchParams();
    if (restaurantId) params.append('restaurantId', restaurantId);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const q = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/platform/restaurants/overview${q}`);
  },
};

export const restaurantAPI = {
  getCurrent: async () => {
    return apiRequest('/restaurants/me');
  },

  update: async (data) => {
    return apiRequest('/restaurants/me', {
      method: 'PUT',
      body: data,
    });
  },

  getById: async (id) => {
    return apiRequest(`/restaurants/${id}`);
  },

  pairWifi: async () => {
    return apiRequest('/restaurants/me/pair-wifi', {
      method: 'POST'
    });
  },
};

// Menu Items APIs
export const menuItemsAPI = {
  getAll: async (restaurantId = null) => {
    const params = restaurantId ? `?restaurantId=${restaurantId}` : '';
    return apiRequest(`/menu-items${params}`);
  },

  getByCategory: async (category, restaurantId = null) => {
    const params = restaurantId ? `?restaurantId=${restaurantId}` : '';
    return apiRequest(`/menu-items/category/${category}${params}`);
  },

  getById: async (id) => {
    return apiRequest(`/menu-items/${id}`);
  },

  create: async (itemData) => {
    return apiRequest('/menu-items', {
      method: 'POST',
      body: itemData,
    });
  },

  update: async (id, itemData) => {
    return apiRequest(`/menu-items/${id}`, {
      method: 'PUT',
      body: itemData,
    });
  },

  delete: async (id) => {
    return apiRequest(`/menu-items/${id}`, {
      method: 'DELETE',
    });
  },

  toggleEnabled: async (id, enabled) => {
    return apiRequest(`/menu-items/${id}/toggle-enabled`, {
      method: 'PATCH',
      body: { enabled },
    });
  },
};

// Orders APIs
export const ordersAPI = {
  getAll: async (status = null, tableNumber = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (tableNumber) params.append('tableNumber', tableNumber);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/orders${query}`);
  },

  getPending: async () => {
    return apiRequest('/orders/pending');
  },

  getByTable: async (tableNumber) => {
    return apiRequest(`/orders/table/${tableNumber}`);
  },

  getById: async (id) => {
    return apiRequest(`/orders/${id}`);
  },

  create: async (orderData) => {
    return apiRequest('/orders', {
      method: 'POST',
      body: orderData,
    });
  },

  updateStatus: async (id, status) => {
    return apiRequest(`/orders/${id}/status`, {
      method: 'PATCH',
      body: { status },
    });
  },

  delete: async (id) => {
    return apiRequest(`/orders/${id}`, {
      method: 'DELETE',
    });
  },

  generateBill: async (orderIds) => {
    return apiRequest('/orders/generate-bill', {
      method: 'POST',
      body: { orderIds },
    });
  },
};

// Bills API
export const billsAPI = {
  getAll: async (from = null, to = null) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/bills${query}`);
  },

  exportCsv: async (from = null, to = null) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';

    const token = getToken();
    const url = API_BASE_URL.endsWith('/')
      ? `${API_BASE_URL}bills/export${query}`
      : `${API_BASE_URL}/bills/export${query}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Failed to export bills: ${response.statusText}`);
    }

    const blob = await response.blob();
    return blob;
  },

  getStats: async () => {
    return apiRequest('/bills/stats');
  },
};

// Waiter calls API
export const waiterAPI = {
  getPending: async () => {
    return apiRequest('/waiter-calls');
  },
  acknowledge: async (id) => {
    return apiRequest(`/waiter-calls/${id}/acknowledge`, {
      method: 'PATCH',
    });
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async (restaurantId = null) => {
    const resolvedRestaurantId = restaurantId || getRestaurantId();
    const params = resolvedRestaurantId ? `?restaurantId=${resolvedRestaurantId}` : '';
    return apiRequest(`/categories${params}`);
  },

  update: async (categories) => {
    return apiRequest('/categories', {
      method: 'PUT',
      body: { categories },
    });
  },
};

// Export utility functions
export { getToken, setToken, removeToken, getRestaurantId, setRestaurantId };

